import type { MessageView } from "../types/ui";

/** Keep headers when a reply, day boundary, or another timeline item intervenes. */
export function groupsWithPrevious(current: MessageView, previous?: MessageView): boolean {
  if (!previous || current.authorXid !== previous.authorXid || current.date !== previous.date
    || current.replyTo || current.kind === "secret" || previous.kind === "secret") return false;
  // Use real timestamps, never locale-dependent display strings.
  const gap = Date.parse(current.createdAt ?? "") - Date.parse(previous.createdAt ?? "");
  return Number.isFinite(gap) && gap >= 0 && gap < 5 * 60_000;
}

export function safeMessageUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return (url.protocol === "https:" || url.protocol === "http:") && !url.username && !url.password
      ? url.href : null;
  } catch { return null; }
}

export function isGifUrl(value: string): boolean {
  const url = safeMessageUrl(value);
  return url !== null && url.startsWith("https:") && /\.(gif|webp)$/i.test(new URL(url).pathname);
}

/** Strip prose punctuation without breaking balanced URL parentheses. */
export function splitUrlPunctuation(value: string): [string, string] {
  let end = value.length;
  while (end > 0 && /[.,!?;:]/.test(value[end - 1]!)) end--;
  while (value[end - 1] === ")" && (value.slice(0, end).match(/\)/g)?.length ?? 0)
    > (value.slice(0, end).match(/\(/g)?.length ?? 0)) end--;
  return [value.slice(0, end), value.slice(end)];
}

export function linkInviteToken(value: string): string | null {
  const safe = safeMessageUrl(value);
  if (!safe) return null;
  const url = new URL(safe);
  const local = ["localhost", "127.0.0.1"].includes(url.hostname);
  if (url.hostname !== "app.trylink.org" && !local) return null;
  return /^\/invite\/([A-Za-z0-9_-]+)\/?$/.exec(url.pathname)?.[1] ?? null;
}

/** Presentation-only cleanup of the exact legacy invite template. */
export function messageDisplayBody(body: string): string {
  const legacy = /^Join [^\r\n]+ on Link: (https?:\/\/\S+)\s*$/.exec(body);
  return legacy?.[1] && linkInviteToken(legacy[1]) ? legacy[1] : body;
}

export const IMAGE_PREVIEW_MIMES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp", "image/avif"]);
